import { FeatureGate } from "@/components/feature-gate";
import { useAuth, useCanWrite } from "@/lib/auth";
import type { FeatureFlags } from "@/lib/features";
import { useProfileConfig } from "@/lib/use-profile-config";
import { Loader2 } from "lucide-react";
import { Redirect } from "wouter";

export interface GatedRouteProps {
  feature: keyof FeatureFlags;
  Component: React.ComponentType<{ params?: Record<string, string | undefined>; }>;
  params?: Record<string, string | undefined>;
  /**
   * OCT-16925 / OCT-17302 - marks a create page as closed to a Viewer. The value is the list page
   * a Viewer is sent to instead, so a bookmarked or shared create URL never
   * renders a form the server will refuse to save.
   *
   * @spec ILLEGAL E157
   */
  writerOnly?: string;
}

/** A route behind a workspace feature flag, and optionally behind the writer-role rule. */
export function GatedRoute({ feature, Component, params, writerOnly }: GatedRouteProps) {
  const { data: profileConfig } = useProfileConfig();
  const { isLoading: authLoading } = useAuth();
  const canWrite = useCanWrite();
  const profileState = profileConfig?.featureStates?.[feature];
  const upsell = profileConfig?.upsellPrompts?.find((p) => p.featureKey === feature);

  if (writerOnly !== undefined) {
    // Never decide on a half-loaded session: no early redirect, no form flash.
    if (authLoading) {
      return (
        <div className="flex items-center justify-center py-24" data-testid="gated-route-auth-loading">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (!canWrite) return <Redirect to={writerOnly} replace />;
  }

  return (
    <FeatureGate
      feature={feature}
      {...(profileState !== undefined && { profileState })}
      {...(upsell?.message !== undefined && { upgradeMessage: upsell.message })}
      {...(upsell?.targetTier !== undefined && { tierRequired: upsell.targetTier })}
    >
      <Component {...(params !== undefined && { params })} />
    </FeatureGate>
  );
}
